import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <motion.header
        data-testid="nav-bar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "glass-panel border-b border-gold/10" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16 py-4">
          <a href="#home" data-testid="nav-logo" className="flex items-center gap-3 group">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/95 p-1 ring-1 ring-gold/40 transition-shadow duration-500 group-hover:shadow-[0_0_24px_rgba(212,175,55,0.35)]">
              <img src="/assets/faithh-logo.png" alt="Faithh Business Consultancy logo" className="h-8 w-8 object-contain" />
            </span>
            <span className="leading-none">
              <span className="block font-display text-lg font-bold tracking-wide text-slate-50">FAITHH</span>
              <span className="block font-mono text-[9px] uppercase tracking-[0.32em] text-gold-soft/90">Business Consultancy</span>
            </span>
          </a>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="relative text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-gold-soft after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-[width] after:duration-400 hover:after:w-full"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="#contact"
              data-testid="nav-cta-button"
              className="btn-gold hidden items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-navy-950 transition-[box-shadow,transform] duration-400 hover:shadow-[0_10px_36px_-8px_rgba(212,175,55,0.55)] hover:-translate-y-0.5 lg:inline-flex"
            >
              Get Free Consultation
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/25 text-gold-soft lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] flex flex-col bg-navy-950/95 backdrop-blur-2xl lg:hidden"
          >
            <div className="flex items-center justify-between px-4 sm:px-8 py-4">
              <span className="font-display text-lg font-bold text-slate-50">FAITHH</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/25 text-gold-soft"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {NAV_LINKS.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  className="border-b border-white/5 py-4 font-display text-3xl font-medium text-slate-200 transition-colors hover:text-gold-soft"
                >
                  {link.label}
                </motion.a>
              ))}
              <motion.a
                href="#contact"
                data-testid="mobile-nav-cta"
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.55 }}
                className="btn-gold mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-base font-semibold text-navy-950"
              >
                Get Free Consultation
                <ArrowUpRight className="h-5 w-5" />
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
